import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Flex, ColorPicker } from '@nula/core-ui';
import styles from './color-picker-area.module.css';

// ── CVA definition ─────────────────────────────────────────────────────────

const colorPickerArea = cva(styles.base, {
  variants: {
    indent: {
      sm: styles.indentSm,
      md: styles.indentMd,
    },
  },
  defaultVariants: {
    indent: 'sm',
  },
});

// ── Types ──────────────────────────────────────────────────────────────────

type ColorPickerAreaVariants = VariantProps<typeof colorPickerArea>;

interface ColorPickerAreaProps extends ColorPickerAreaVariants {
  /** Initial color — uncontrolled so API round-trips don't reset drag state */
  defaultValue: string;
  /** Palette colors shown as presets inside the picker */
  colors: string[];
  /** Called when a color is selected */
  onChange: (color: string) => void;
  /** Additional CSS class */
  className?: string;
}

// ── ColorPickerArea ────────────────────────────────────────────────────────

/**
 * ColorPickerArea — shared color selection UI used in calendar subscription
 * rows (desktop filter dropdown + mobile filter drawer).
 *
 * Used in CalendarPage desktop (indent="sm") and mobile (indent="md").
 */
export function ColorPickerArea({
  defaultValue,
  colors,
  onChange,
  indent = 'sm',
  className,
}: ColorPickerAreaProps) {
  return (
    <Flex
      vertical
      gap="sm"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      className={colorPickerArea({ indent, className })}
    >
      <ColorPicker
        defaultValue={defaultValue}
        onChange={onChange}
        size="sm"
        presets={[{ label: 'Presets', colors }]}
      />
    </Flex>
  );
}
